// @ts-nocheck
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
    TextField,
    List,
    ListItem,
    ListItemButton,
    ListItemText,
    Typography,
    Box,
    CircularProgress,
    InputAdornment,
    IconButton,
    ClickAwayListener,
    Paper,
    Grid,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import PersonIcon from '@mui/icons-material/Person';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import EventIcon from '@mui/icons-material/Event';
import { teal } from 'api';

const OmniSearch = () => {
    const [query, setQuery] = useState('');
    const [results, setResults] = useState(null);
    const [loading, setLoading] = useState(false);
    const [showResults, setShowResults] = useState(false);
    const navigate = useNavigate();
    const { organisationId } = useParams();
    const debounceTimerRef = useRef(null);

    // Restore search state on mount
    useEffect(() => {
        const savedQuery = sessionStorage.getItem('omniSearchQuery');
        const savedResults = sessionStorage.getItem('omniSearchResults');

        if (savedQuery && savedResults) {
            setQuery(savedQuery);
            setResults(JSON.parse(savedResults));
            setShowResults(true);
        }
    }, []);

    // Cleanup debounce timer on unmount
    useEffect(() => () => {
        if (debounceTimerRef.current) {
            clearTimeout(debounceTimerRef.current);
        }
    }, []);

    const handleSearch = async (searchQuery) => {
        if (!searchQuery.trim() || !organisationId) {
            setResults(null);
            setShowResults(false);
            sessionStorage.removeItem('omniSearchQuery');
            sessionStorage.removeItem('omniSearchResults');
            return;
        }

        setLoading(true);
        setShowResults(true);
        try {
            const data = await teal.omniSearch(organisationId, searchQuery);
            setResults(data);
            sessionStorage.setItem('omniSearchQuery', searchQuery);
            sessionStorage.setItem('omniSearchResults', JSON.stringify(data));
        } catch (error) {
            console.error('Search failed:', error);
            setResults(null);
        } finally {
            setLoading(false);
        }
    };

    const handleQueryChange = (e) => {
        const newQuery = e.target.value;
        setQuery(newQuery);

        // Clear existing timer
        if (debounceTimerRef.current) {
            clearTimeout(debounceTimerRef.current);
        }

        // Set new timer
        debounceTimerRef.current = setTimeout(() => {
            handleSearch(newQuery);
        }, 300);
    };

    const handleClose = () => {
        setShowResults(false);
    };

    const handleUserClick = (user) => {
        navigate(`/${organisationId}/access/users/${user.userId}`);
        // Don't clear state anymore - keep it in sessionStorage
        setShowResults(false);
    };

    const handleOrderClick = (order) => {
        navigate(`/${organisationId}/access/orders/${order.orderId}`);
        // Don't clear state anymore - keep it in sessionStorage
        setShowResults(false);
    };

    const handleMeetingClick = (meeting) => {
        navigate(`/${organisationId}/access/orders/${meeting.order.orderId}`);
        // Don't clear state anymore - keep it in sessionStorage
        setShowResults(false);
    };

    const handleClearSearch = () => {
        setQuery('');
        setResults(null);
        setShowResults(false);
        sessionStorage.removeItem('omniSearchQuery');
        sessionStorage.removeItem('omniSearchResults');
    };

    return (
        <ClickAwayListener onClickAway={ handleClose }>
            <Box sx={{ position: 'relative', width: '100%' }}>
                <TextField
                    fullWidth
                    size="small"
                    placeholder="Søg alt..."
                    value={ query }
                    onChange={ handleQueryChange }
                    onFocus={ () => query && setShowResults(true) }
                    autoComplete="off"
                    sx={{
                        backgroundColor: 'white',
                        borderRadius: 1,
                        paddingLeft:1,
                        paddingRight:1,
                        '& .MuiOutlinedInput-root': {
                            '& fieldset': {
                                borderColor: 'rgba(0, 0, 0, 0.23)',
                            },
                            '&:hover fieldset': {
                                borderColor: 'rgba(0, 0, 0, 0.87)',
                            },
                            '&.Mui-focused fieldset': {
                                borderColor: 'primary.main',
                            },
                        },
                    }}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon />
                            </InputAdornment>
                        ),
                        endAdornment: query && (
                            <InputAdornment position="end">
                                <IconButton
                                    size="small"
                                    onClick={ handleClearSearch }
                                >
                                    <CloseIcon fontSize="small" />
                                </IconButton>
                            </InputAdornment>
                        ),
                    }}
                />

                { showResults && (
                    <Paper
                        sx={{
                            position: 'absolute',
                            top: '100%',
                            left: 0,
                            width: '600px',
                            mt: 1,
                            maxHeight: '70vh',
                            overflow: 'auto',
                            zIndex: 1300,
                        }}
                    >
                        { loading && (
                            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                                <CircularProgress size={ 24 } />
                            </Box>
                        ) }

                        { !loading && results && (
                            <Box>
                                { results.count.total === 0 ? (
                                    <Typography color="text.secondary" sx={{ textAlign: 'center', p: 3 }}>
                                        Ingen resultater fundet
                                    </Typography>
                                ) : (
                                    <Grid container spacing={ 0 }>
                                        <Grid item xs={ 4 } sx={{ borderRight: '1px solid', borderColor: 'divider' }}>
                                            <Box>
                                                <Typography variant="subtitle2" color="text.secondary" sx={{ px: 2, py: 1, backgroundColor: 'grey.50' }}>
                                                    <PersonIcon sx={{ fontSize: 16, mr: 1, verticalAlign: 'middle' }} />
                                                    Brugere ({ results.count.users })
                                                </Typography>
                                                { results.users && results.users.length > 0 ? (
                                                    <List dense sx={{ maxHeight: 'calc(70vh - 48px)', overflow: 'auto' }}>
                                                        { results.users.filter(user => user.userId).map((user) => (
                                                            <ListItem key={ user.userId } disablePadding>
                                                                <ListItemButton onClick={ () => handleUserClick(user) }>
                                                                    <ListItemText
                                                                        primary={ user.name }
                                                                        secondary={ user.email }
                                                                    />
                                                                </ListItemButton>
                                                            </ListItem>
                                                        )) }
                                                    </List>
                                                ) : (
                                                    <Typography color="text.secondary" sx={{ textAlign: 'center', p: 2, fontSize: '0.875rem' }}>
                                                        Ingen brugere fundet
                                                    </Typography>
                                                ) }
                                            </Box>
                                        </Grid>

                                        <Grid item xs={ 4 } sx={{ borderRight: '1px solid', borderColor: 'divider' }}>
                                            <Box>
                                                <Typography variant="subtitle2" color="text.secondary" sx={{ px: 2, py: 1, backgroundColor: 'grey.50' }}>
                                                    <ShoppingCartIcon sx={{ fontSize: 16, mr: 1, verticalAlign: 'middle' }} />
                                                    Ordrer ({ results.count.orders >= 10 ? '10+' : results.count.orders })
                                                </Typography>
                                                { results.orders && results.orders.length > 0 ? (
                                                    <List dense sx={{ maxHeight: 'calc(70vh - 48px)', overflow: 'auto' }}>
                                                        { results.orders.map((order) => (
                                                            <ListItem key={ order.orderId } disablePadding>
                                                                <ListItemButton onClick={ () => handleOrderClick(order) }>
                                                                    <ListItemText
                                                                        primary={ order.customer?.name || `${order.customer?.firstName || ''} ${order.customer?.lastName || ''}`.trim() }
                                                                        secondary={ `Ordre #${order.orderNumber}` }
                                                                    />
                                                                </ListItemButton>
                                                            </ListItem>
                                                        )) }
                                                    </List>
                                                ) : (
                                                    <Typography color="text.secondary" sx={{ textAlign: 'center', p: 2, fontSize: '0.875rem' }}>
                                                        Ingen ordrer fundet
                                                    </Typography>
                                                ) }
                                            </Box>
                                        </Grid>

                                        <Grid item xs={ 4 }>
                                            <Box>
                                                <Typography variant="subtitle2" color="text.secondary" sx={{ px: 2, py: 1, backgroundColor: 'grey.50' }}>
                                                    <EventIcon sx={{ fontSize: 16, mr: 1, verticalAlign: 'middle' }} />
                                                    Møder ({ results.count.meetings })
                                                </Typography>
                                                { results.meetings && results.meetings.length > 0 ? (
                                                    <List dense sx={{ maxHeight: 'calc(70vh - 48px)', overflow: 'auto' }}>
                                                        { results.meetings.map((meeting, index) => (
                                                            <ListItem key={ meeting.booking?.eventId || index } disablePadding>
                                                                <ListItemButton onClick={ () => handleMeetingClick(meeting) }>
                                                                    <ListItemText
                                                                        primary={ meeting.booking?.startTime ? new Date(meeting.booking.startTime).toLocaleString('da-DK', {
                                                                            dateStyle: 'short',
                                                                            timeStyle: 'short',
                                                                        }) : 'Ingen dato' }
                                                                        secondary={ `${meeting.order?.customer?.firstName || ''} ${meeting.order?.customer?.lastName || ''}`.trim() || 'Ingen kunde' }
                                                                    />
                                                                </ListItemButton>
                                                            </ListItem>
                                                        )) }
                                                    </List>
                                                ) : (
                                                    <Typography color="text.secondary" sx={{ textAlign: 'center', p: 2, fontSize: '0.875rem' }}>
                                                        Ingen møder fundet
                                                    </Typography>
                                                ) }
                                            </Box>
                                        </Grid>
                                    </Grid>
                                ) }
                            </Box>
                        ) }
                    </Paper>
                ) }
            </Box>
        </ClickAwayListener>
    );
};

export default OmniSearch;
